/**
 * Consumer-facing hooks. Two of them:
 *   - useT(namespace) — namespaced translation. Enforces that every caller
 *     names its namespace up front so no service package accidentally reads
 *     from another's keys. Wraps react-i18next's useTranslation.
 *   - useFormat() — Intl.NumberFormat / DateTimeFormat / RelativeTimeFormat
 *     wired to the current language's locale. Never hand-format numbers or
 *     dates; use this so RU/KZ separators land correctly.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { descriptor } from './config.js';
import { useLang } from './provider.js';

/** Namespaced translation. Example:
 *    const t = useT('antifraud');
 *    <h1>{t('formats.title')}</h1>       // reads locales/{lang}/antifraud.json
 *  Consumer packages should call registerNamespace() at import time so the
 *  json is loaded before the first render — otherwise the fallback (the
 *  raw key string) will flash. */
export function useT(namespace: string): (key: string, opts?: Record<string, unknown>) => string {
  const { t } = useTranslation(namespace);
  // Cast: i18next allows arbitrary return types via generics. We commit to
  // string-only usage in this codebase.
  return t as (key: string, opts?: Record<string, unknown>) => string;
}

export interface Formatters {
  number: (n: number, opts?: Intl.NumberFormatOptions) => string;
  currency: (amount: number, code: string) => string;
  date: (d: string | number | Date, opts?: Intl.DateTimeFormatOptions) => string;
  dateTime: (d: string | number | Date) => string;
  time: (d: string | number | Date) => string;
  relative: (from: Date, to?: Date) => string;
}

/** Locale-aware Intl formatters. Memoized on the current language so RU
 *  users get "50 000,50 ₸", KK "50 000,50 KZT", EN "50,000.50 KZT" — all
 *  from the same call. */
export function useFormat(): Formatters {
  const { lang } = useLang();
  const locale = descriptor(lang).intlLocale;

  return useMemo<Formatters>(() => {
    const numberFmt = new Intl.NumberFormat(locale);
    const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
    const dateTimeFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
    const timeFmt = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
    const relFmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    return {
      number: (n, opts) => (opts ? new Intl.NumberFormat(locale, opts).format(n) : numberFmt.format(n)),
      currency: (amount, code) =>
        new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount),
      date: (d, opts) =>
        opts ? new Intl.DateTimeFormat(locale, opts).format(new Date(d)) : dateFmt.format(new Date(d)),
      dateTime: (d) => dateTimeFmt.format(new Date(d)),
      time: (d) => timeFmt.format(new Date(d)),
      relative: (from, to = new Date()) => {
        const diffSec = Math.round((from.getTime() - to.getTime()) / 1000);
        const abs = Math.abs(diffSec);
        if (abs < 60) return relFmt.format(diffSec, 'second');
        if (abs < 3600) return relFmt.format(Math.round(diffSec / 60), 'minute');
        if (abs < 86400) return relFmt.format(Math.round(diffSec / 3600), 'hour');
        return relFmt.format(Math.round(diffSec / 86400), 'day');
      },
    };
  }, [locale]);
}
